<template>
  <div class="sidebar" :class="{ 'sidebar--active': active }">
    <aside ref="sidebarContent" class="sidebar-content">
      <button class="close-button" @click="closeSidebar">
        <CloseIcon class="icon" />
      </button>
      <nav class="nav">
        <StyledCollapse :items="menu?.items || []">
          <template #link="{ item, elClass }">
            <BaseLink
              v-if="item.link"
              :link="item.link"
              class="t-menu-1"
              :class="elClass"
              @click="closeSidebar"
            >
              {{ item.label }}
            </BaseLink>
            <span v-else class="t-menu-1" :class="elClass">
              {{ item.label }}
            </span>
          </template>

          <template #default="{ subItem, elClass, isListActive }">
            <NavigationSubItem
              :class="elClass"
              :item="subItem"
              :tabindex="isListActive ? undefined : -1"
              @click="closeSidebar"
            />
          </template>
        </StyledCollapse>
        <NavigationLanguageSwitcher />
      </nav>
    </aside>
  </div>
</template>

<script setup lang="ts">
import CloseIcon from '@/assets/icons/close.svg?component'
import type { PublicMainMenu } from '@/service/__generated-api'

const emit = defineEmits<{ (event: 'close'): void }>()
const props = withDefaults(
  defineProps<{ menu: Partial<PublicMainMenu> | null; active?: boolean }>(),
  {
    active: false
  }
)
const sidebarContent = ref<HTMLElement>()
const { blockScroll, unblockScroll } = useScrollLock(sidebarContent)

watch(
  () => props.active,
  () => (props.active ? blockScroll() : unblockScroll())
)

const closeSidebar = () => {
  unblockScroll()
  emit('close')
}
</script>

<style lang="scss" scoped>
.sidebar {
  @include hide-up(s-lg);
  position: fixed;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  transition: background-color $transition;

  &--active {
    pointer-events: auto;
    background-color: rgba($layout-background-02, 0.8);
  }
}

.sidebar-content {
  @include scrollbar-mask;
  @include flex-col;
  position: relative;
  width: 90%;
  max-width: rem(499px);
  height: 100%;
  padding: rem(80px) rem(80px) rem(80px) rem(30px);
  margin-left: auto;
  overflow-y: auto;
  background-color: $layout-background-02;
  box-shadow: $base-shadow;
  transition: transform $dynamic-transition;

  transform: translatex(100%);

  @include media-up(md) {
    padding: rem(140px) rem(140px) rem(140px) rem(60px);
  }

  .sidebar--active & {
    transform: translateX(0);
  }
}

.close-button {
  position: absolute;
  top: rem(39px - 11px);
  right: rem(37px - 11px);
  padding: rem(11px);

  @include media-up(md) {
    right: rem(60px - 11px);
  }

  .icon {
    display: block;
  }
}

.nav {
  @include flex-col;
  gap: rem(40px);
}
</style>
